// AIAssistant uses dynamic message parsing from web worker with varied formats
// that cannot be statically typed. Using 'any' is appropriate for this use case.
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { createContactSupportButton } from '@/components/legacy-ui/utils';
import { Loader } from '@/components/Loader';
import { useAIAgents } from '@/hooks/useAIAgents';
import {
  useGetChildConversations,
  useGetConversationById,
  useGetConversationTraces,
} from '@/hooks/useChat';
import { useChatWorker } from '@/hooks/useChatWorker';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useFlag } from '@/hooks/useFlags';
import { useCreateHunt, useHunts } from '@/sections/insights/hunts/useHunts';
import { useAuth } from '@/state/auth';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

import { HuntStatusDrawer } from './components/AutoDrawer';
import { HeaderBar } from './components/HeaderBar';
import { InputForm } from './components/InputForm';
import { LaunchHannibalDialog } from './components/LaunchHannibalDialog';
import { MessageArea } from './components/MessageArea';
import { Sidebar } from './components/Sidebar';
import { TraceDrawer } from './components/TraceDrawer';
import { WelcomeView } from './components/WelcomeView';
import {
  clampAgentToMode,
  ConvState,
  deriveConvState,
} from './config/deriveConvState';
import { saveConversationMode } from './config/storage';
import { MAX_TEXTAREA_HEIGHT } from './constants/limits';
import ConversationJobs from './ConversationJobs';
import { useAgentExecution } from './hooks/useAgentExecution';
import { useConversationActiveJobs } from './hooks/useConversationActiveJobs';
import { useConversationManager } from './hooks/useConversationManager';
import { useConversationSwitch } from './hooks/useConversationSwitch';
import { useHuntMode } from './hooks/useHuntMode';
import { useMessageHistory } from './hooks/useMessageHistory';
import { useMessageSending } from './hooks/useMessageSending';
import { usePopoverDismiss } from './hooks/usePopoverDismiss';
import { useScrollBehavior } from './hooks/useScrollBehavior';
import { useSummon } from './hooks/useSummon';
import { useTraceDrawerAutoOpen } from './hooks/useTraceDrawerAutoOpen';
import { useVoiceInput } from './hooks/useVoiceInput';
import { useAutoDrawerStore } from './stores/autoDrawer.store';
import { useTraceDrawerStore } from './stores/traceDrawer.store';
import type { AIAssistantMode, SidebarTab } from './types';
import { useCommandMenu } from './useCommandMenu';

export function AIAssistant() {
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get('conversationId');
  const summonMessage = searchParams.get('summonMessage');
  const summonMode = searchParams.get('summonMode') as 'query' | 'agent' | null;

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useStorage(
    { key: StorageKey.AI_ASSISTANT_SIDEBAR_COLLAPSED },
    true
  );
  const [activeTab, setActiveTab] = useState<SidebarTab>('conversations');

  const [newMessage, setNewMessage] = useState('');

  // modeChangeRef is owned here and threaded into useConversationManager
  // (writer) and useConversationSwitch (reader). It carries the new-chat hunt
  // ('auto') intent for the switch effect only — convState derivation is
  // deliberately ref-free (see deriveConvState).
  const modeChangeRef = useRef<'auto' | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const commandMenuRef = useRef<HTMLDivElement>(null);

  const { isPraetorianUser, whoami } = useAuth();
  const {
    locked: aiAssistantLocked,
    lockTitle,
    lockDescription,
    featureName,
    isLoading: featureLockLoading,
  } = useFeatureLock('aiAssistant');
  const { disableAIInference, enableAIAgents, huntVisibility } = useFlag();

  const isAIDisabledButBypassed = disableAIInference && !aiAssistantLocked;
  const { data: agents } = useAIAgents();
  const { data: hunts = [] } = useHunts();

  const isAtLeastAnalyst =
    whoami?.role === 'analyst' || whoami?.role === 'admin';
  const canUseAgentMode =
    (isPraetorianUser && isAtLeastAnalyst) ||
    (enableAIAgents && isAtLeastAnalyst);

  // Presentation-only gate for the Hunts tab affordance. Mirrors the server's
  // HuntsVisible decision (Praetorian OR (flag AND analyst/admin role)) so a
  // customer who would only ever see an empty hunt surface never gets a
  // dead-end Hunts tab. NOT a data gate: useHunts still calls the server, which
  // remains the source of truth — a wrong-positive here only exposes the
  // already-empty view the server returns; a wrong-negative just hides a tab
  // that would have been empty anyway. Same shape as canUseAgentMode.
  const canViewHunts = isPraetorianUser || (huntVisibility && isAtLeastAnalyst);

  // Single source of truth for the active chat conversation's mode + agent,
  // keyed by the conversation it describes. Because convState.convId is derived
  // from the same conversationId it must agree with, mode can NEVER lag
  // conversationId across a router transition (the torn-state class is
  // unrepresentable). See the adjust-during-render block below.
  const [convState, setConvState] = useState<ConvState>(() =>
    deriveConvState(conversationId, { canUseAgentMode })
  );

  // 'auto' is an APP VIEW (the Hunt section), not a per-conversation mode.
  // Orthogonal flag so it can never be persisted against a chat conversation.
  const [isHuntView, setIsHuntView] = useState(false);

  // Adjust state during render (React's supported store-prev-render technique):
  // re-derive synchronously when the URL conversation changes, so the set makes
  // the guard false and convergence happens in the same commit (no extra paint,
  // no tear). The second clause makes the agent-unavailable downgrade reactive
  // (when canUseAgentMode flips false, re-derive heals 'agent' -> 'query').
  if (
    conversationId !== convState.convId ||
    (convState.mode === 'agent' && !canUseAgentMode)
  ) {
    setConvState(deriveConvState(conversationId, { canUseAgentMode }));
  }

  // Composite read union for back-compat child props/readers. Writers never use
  // setMode; they call the write-through helpers below.
  const mode: AIAssistantMode = isHuntView ? 'auto' : convState.mode;
  const activeAgent = convState.agent;

  // Deliberate chat-mode change (user toggle / slash command / welcome). Updates
  // the single store AND persists by id atomically. Leaves the hunt view.
  const applyChatMode = (next: 'query' | 'agent') => {
    setIsHuntView(false);
    setConvState(s => ({
      ...s,
      mode: next,
      agent: clampAgentToMode(next, s.agent),
    }));
    if (conversationId) {
      saveConversationMode(
        conversationId,
        next,
        clampAgentToMode(next, convState.agent) ?? undefined
      );
    }
  };

  // Deliberate active-agent change (@agent / /agent:). Setting an agent implies
  // agent mode. Persists by id when an agent is set against a conversation.
  const applyActiveAgent = (agentName: string | null) => {
    setConvState(s => ({
      ...s,
      agent: agentName,
      mode: agentName ? 'agent' : s.mode,
    }));
    if (conversationId && agentName) {
      saveConversationMode(conversationId, 'agent', agentName);
    }
  };

  const {
    conversations,
    messages,
    messagesLoading,
    pendingMessages: workerPendingMessages,
    sendMessage: sendMessageWorker,
    createConversation: createConversationWorkerRaw,
    compactConversation: compactConversationWorker,
    stopConversation: stopConversationWorker,
  } = useChatWorker(conversationId || undefined);

  const {
    setMessageHistory,
    setHistoryIndex,
    setSavedMessage,
    handleHistoryNavigation,
  } = useMessageHistory({ newMessage, setNewMessage });

  const { mutate: createHunt, isPending: isHuntPending } = useCreateHunt();
  const huntMode = useHuntMode({
    setNewMessage,
    agents,
    createHunt,
    isHuntPending,
  });

  const {
    showCommandMenu,
    filteredCommands,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    replaceCurrentWord,
    menuType,
  } = useCommandMenu(
    newMessage,
    canUseAgentMode,
    applyChatMode,
    () => setNewMessage(''),
    () => {},
    agents,
    convState.mode,
    (mission: string) => {
      huntMode.setPendingMission(mission);
    }
  );

  // useMessageSending is called before useConversationManager so that
  // setIsWaitingForResponse is available to thread into the conversation handlers.
  const {
    failedMessage,
    setFailedMessage,
    isWaitingForResponse,
    setIsWaitingForResponse,
    handleSendMessage,
    handleExampleClick,
    createConversationWorker,
  } = useMessageSending({
    newMessage,
    setNewMessage,
    conversationId,
    mode,
    activeAgent,
    applyActiveAgent,
    isInputDisabled: false,
    sendMessageWorker,
    createConversationWorkerRaw,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    filteredCommands,
    setMessageHistory,
    setHistoryIndex,
    setSavedMessage,
    textareaRef,
  });

  // Navigation handlers (handleModeChange, handleCreateConversation,
  // handleSelectConversation) and modeChangeRef live in useConversationManager.
  const {
    conversationPreviews,
    modeFilteredConversations,
    handleDeleteConversation,
    handleModeChange,
    handleCreateConversation,
    handleSelectConversation,
  } = useConversationManager({
    conversations,
    messages,
    conversationId,
    applyChatMode,
    setActiveTab,
    setIsHuntView,
    setIsWaitingForResponse,
    isInputDisabled: false,
    modeChangeRef,
  });

  const { isLoadingConversation } = useConversationSwitch({
    conversationId,
    setActiveTab,
    setIsHuntView,
    isAIDisabledButBypassed,
    disableAIInference,
    summonMessage,
    messages,
    messagesLoading,
    modeChangeRef,
    historySetters: { setMessageHistory, setHistoryIndex, setSavedMessage },
    messagingSetters: { setFailedMessage, setIsWaitingForResponse },
  });

  const {
    agentExecutions,
    expandedAgentCards,
    loadingStatus,
    thinkingStartTime,
    toggleAgentCard,
  } = useAgentExecution({
    messages,
    activeAgent,
    conversationId,
    agents,
  });

  const { isRecording, toggleRecording } = useVoiceInput({
    setNewMessage,
  });

  const {
    showScrollToBottom,
    scrollContainerRef,
    messagesEndRef,
    scrollToBottom,
  } = useScrollBehavior({
    messagesLength: messages.length,
    workerPendingMessagesLength: workerPendingMessages.length,
    conversationId,
  });

  useSummon({
    summonMessage,
    summonMode,
    isAIDisabledButBypassed,
    // Pending-chat write-through: set the mode for the about-to-be-created
    // conversation. No persist here (no id yet); useSummon persists against the
    // freshly-created newId itself. The subsequent setSearchParams(newId) flips
    // conversationId, which the adjust-during-render block re-derives from.
    setPendingChatMode: (next: 'query' | 'agent') => {
      setIsHuntView(false);
      setConvState(s => ({
        ...s,
        mode: next,
        agent: clampAgentToMode(next, s.agent),
      }));
    },
    createConversation: createConversationWorker,
    setSearchParams,
    setFailedMessage,
  });

  const {
    showToolCallsPopover,
    setShowToolCallsPopover,
    showTipsMenu,
    setShowTipsMenu,
  } = usePopoverDismiss();

  const { data: selectedConversationById } = useGetConversationById(
    conversationId || undefined
  );

  const selectedConversation = conversationId
    ? conversations.find(
        c =>
          c.uuid === conversationId ||
          c.id === conversationId ||
          c.key?.includes(conversationId)
      ) || selectedConversationById
    : undefined;

  const parentConversationId = selectedConversation?.parent_id;
  const isSubagent = !!parentConversationId && parentConversationId !== 'self';
  const subagentStatus = selectedConversation?.status;

  const isSubagentQueued = isSubagent && subagentStatus === 'queued';
  const isSubagentFinished = isSubagent && subagentStatus === 'idle';
  const subagentAnchorId = isSubagent
    ? parentConversationId
    : conversationId || undefined;
  const { data: childConversations } =
    useGetChildConversations(subagentAnchorId);

  const { data: traces } = useGetConversationTraces(
    selectedConversation?.trace_id
  );

  useTraceDrawerAutoOpen({
    traceId: selectedConversation?.trace_id,
    traces,
    enabled: !!conversationId,
  });

  const openTraceDrawer = useTraceDrawerStore(s => s.open);
  const closeTraceDrawer = useTraceDrawerStore(s => s.close);

  const activeHuntUuid = useAutoDrawerStore(s => s.huntUuid);

  React.useEffect(() => {
    return () => {
      closeTraceDrawer();
    };
  }, [closeTraceDrawer]);

  const isInputDisabled =
    aiAssistantLocked ||
    isAIDisabledButBypassed ||
    isSubagentQueued ||
    isSubagentFinished;

  // Background capability jobs (status JQ/JR) spawned by the agent keep running
  // after the conversation planner goes idle. Surface them so the stop button
  // stays available. Polling is gated on conversationId inside the hook (no
  // worker runs when no conversation is selected). Cancellation is owned by the
  // backend stop endpoint (see handleStop), not the client.
  const {
    hasActiveJobs,
    jobs: activeJobs,
    isLoading: isLoadingActiveJobs,
  } = useConversationActiveJobs(conversationId || undefined);

  // Planner-only signal: the conversation planner is doing work when we're
  // awaiting a first response, the message stream shows it thinking, or the
  // conversation row is still active. This intentionally EXCLUDES hasActiveJobs
  // so a running planner (Send hidden, Stop shown) is decoupled from background
  // jobs (Send stays available).
  const isPlannerRunning =
    isWaitingForResponse ||
    loadingStatus !== '' ||
    selectedConversation?.status === 'active';

  // Background capability jobs keep the Stop available even when the planner is
  // idle — without hiding Send. Gated on !isPlannerRunning so the planner-running
  // case dominates and we never render a double Stop.
  const showBackgroundStop = !isPlannerRunning && hasActiveJobs;

  const [isStopping, setIsStopping] = React.useState(false);

  // Clear the transient "stopping" state once ALL run signals are gone (planner
  // idle AND no background jobs), otherwise a stale "stopping" pulse can persist.
  React.useEffect(() => {
    if (!isPlannerRunning && !hasActiveJobs) setIsStopping(false);
  }, [isPlannerRunning, hasActiveJobs]);

  // Stop the running agent. The backend `POST /planner/stop` endpoint both
  // writes the planner stop signal AND cancels every non-terminal background
  // job for the conversation server-side (`cancelSubagentJobs` in
  // backend/pkg/handler/handlers/conversation/stop.go) — the job cancellation
  // runs even when the planner is already idle, so no separate client-side
  // cancel is needed. The backend finalizes its partial result and goes idle;
  // the user's next message re-invokes the planner fresh.
  const handleStop = async () => {
    if (!conversationId || isStopping) return;
    setIsStopping(true);
    try {
      await stopConversationWorker(conversationId);
    } catch (err) {
      console.error('Failed to stop conversation', err);
      setIsStopping(false);
    }
  };

  // Compaction: ask the backend to summarize the current conversation with a
  // large model and seed a new conversation with that summary. The backend
  // returns the new (empty) conversation immediately; the summary message
  // arrives later via the standard WebSocket fan-out.
  const handleCompactConversation = async () => {
    if (!conversationId || isInputDisabled) return;
    try {
      const conv = await compactConversationWorker(conversationId);
      const newId =
        conv.uuid || conv.id || (conv.key || '').replace('#conversation#', '');
      if (!newId) return;
      setIsWaitingForResponse(true);
      // Leave the hunt view; the new conversation's mode derives from its
      // conversationId once the transition lands. Never write against the
      // SOURCE conversation (it keeps its real mode) — there is no setMode here,
      // so the transient (source, 'query') corruption is impossible.
      setIsHuntView(false);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('conversationId', newId);
        return newParams;
      });
    } catch (err) {
      console.error('Failed to compact conversation', err);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  const handleTracesCommand = () => {
    if (selectedConversation?.trace_id) {
      openTraceDrawer();
    }
  };

  if (featureLockLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader isLoading type="spinner" />
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-w-0 overflow-hidden">
      <FeatureLock
        locked={aiAssistantLocked}
        title={lockTitle}
        description={lockDescription}
        action={createContactSupportButton(featureName)}
      >
        <Sidebar
          isSidebarCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          mode={mode}
          isAIDisabledButBypassed={isAIDisabledButBypassed}
          isInputDisabled={isInputDisabled}
          conversations={conversations}
          conversationPreviews={conversationPreviews}
          modeFilteredConversations={modeFilteredConversations}
          selectedConversationId={conversationId}
          onCreateConversation={handleCreateConversation}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onExampleClick={handleExampleClick}
          onModeChange={handleModeChange}
          showHunts={!!canViewHunts}
          hunts={hunts}
          activeHuntUuid={activeHuntUuid}
          onNewHunt={() => huntMode.setPendingMission('')}
        />

        <div className="relative flex min-w-0 flex-1 flex-col">
          {conversationId && (
            <HeaderBar
              conversationId={conversationId}
              messages={messages}
              mode={mode}
              isWaitingForResponse={isWaitingForResponse}
              isThinking={loadingStatus !== ''}
              selectedConversation={selectedConversation}
              traces={traces}
              childConversations={childConversations ?? []}
              loadingStatus={loadingStatus}
              agentExecutions={agentExecutions}
              expandedAgentCards={expandedAgentCards}
              thinkingStartTime={thinkingStartTime}
              showToolCallsPopover={showToolCallsPopover}
              setShowToolCallsPopover={setShowToolCallsPopover}
              onTracesCommand={handleTracesCommand}
              onSetPendingMission={mission =>
                huntMode.setPendingMission(mission)
              }
            />
          )}

          {conversationId ? (
            <MessageArea
              conversationId={conversationId}
              messages={messages}
              workerPendingMessages={workerPendingMessages}
              isLoadingConversation={isLoadingConversation}
              failedMessage={failedMessage}
              loadingStatus={loadingStatus}
              isSubagent={isSubagent}
              parentConversationId={parentConversationId}
              selectedConversation={selectedConversation}
              showScrollToBottom={showScrollToBottom}
              scrollContainerRef={scrollContainerRef}
              messagesEndRef={messagesEndRef}
              onScrollToBottom={scrollToBottom}
              agentExecutions={agentExecutions}
              expandedAgentCards={expandedAgentCards}
              toggleAgentCard={toggleAgentCard}
              onCreateConversation={handleCreateConversation}
              onCompactConversation={handleCompactConversation}
            />
          ) : (
            <WelcomeView
              mode={mode}
              canUseAgentMode={canUseAgentMode}
              isPraetorianUser={isPraetorianUser}
              agents={agents}
              isInputDisabled={isInputDisabled}
              isAIDisabledButBypassed={isAIDisabledButBypassed}
              onExampleClick={handleExampleClick}
              onModeChange={handleModeChange}
              setNewMessage={setNewMessage}
              newMessage={newMessage}
              createConversationWorker={createConversationWorker}
              onSetMode={applyChatMode}
              onSetPendingMission={() => huntMode.setPendingMission('')}
              setFailedMessage={setFailedMessage}
            />
          )}

          {/* Warning banner when AI inference is disabled but user bypasses lock */}
          {isAIDisabledButBypassed && conversationId && (
            <div className="mx-6 mb-2 flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-2.5">
              <ExclamationTriangleIcon className="size-5 shrink-0 text-amber-500" />
              <p className="text-chariot-text-secondary text-sm">
                LLM functionality disabled. Administrators can enable AI
                Inference under Settings {'->'} Organization.
              </p>
            </div>
          )}

          {conversationId && (
            <ConversationJobs
              jobs={activeJobs}
              isLoading={isLoadingActiveJobs}
            />
          )}

          {conversationId && (
            <InputForm
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              mode={mode}
              onModeChange={handleModeChange}
              isInputDisabled={isInputDisabled}
              isWaitingForResponse={isWaitingForResponse}
              isPlannerRunning={isPlannerRunning}
              showBackgroundStop={showBackgroundStop}
              isStopping={isStopping}
              onStop={handleStop}
              isRecording={isRecording}
              isAIDisabledButBypassed={isAIDisabledButBypassed}
              canUseAgentMode={canUseAgentMode}
              isPraetorianUser={isPraetorianUser}
              isSubagent={isSubagent}
              isSubagentFinished={isSubagentFinished}
              isSubagentQueued={isSubagentQueued}
              activeAgent={activeAgent}
              textareaRef={textareaRef}
              commandMenuRef={commandMenuRef}
              showCommandMenu={showCommandMenu}
              filteredCommands={filteredCommands}
              menuType={menuType}
              showTipsMenu={showTipsMenu}
              setShowTipsMenu={setShowTipsMenu}
              onSubmit={handleSendMessage}
              onToggleRecording={toggleRecording}
              onSelectCommand={selectCommand}
              onReplaceCurrentWord={replaceCurrentWord}
              onHistoryNavigation={handleHistoryNavigation}
              maxTextareaHeight={MAX_TEXTAREA_HEIGHT}
            />
          )}

          <LaunchHannibalDialog
            open={huntMode.pendingMission !== null}
            mission={huntMode.pendingMission ?? ''}
            onLaunch={huntMode.handleHannibalLaunch}
            onCancel={huntMode.handleHannibalCancel}
            isPending={huntMode.isHuntPending}
            huntAgents={huntMode.huntAgents}
          />

          {isHuntView ? (
            <HuntStatusDrawer />
          ) : (
            <TraceDrawer
              traceId={selectedConversation?.trace_id}
              childConversations={childConversations}
              currentConversationId={conversationId ?? undefined}
              parentConversationId={
                isSubagent ? parentConversationId : undefined
              }
              onNavigateToConversation={id => {
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set('conversationId', id);
                  return newParams;
                });
              }}
            />
          )}
        </div>
      </FeatureLock>
    </div>
  );
}

export default AIAssistant;
